import Tooltip from "@mui/material/Tooltip";
import { NoValueDash } from "_shared/_views/tables/no-value-dash";
import { useMinuteClock } from "_shared/dates/use-minute-clock";
import {
  formatDate,
  formatFullTimestamp,
  formatTimestamp,
  isoDateTime,
  type DateValue,
} from "_shared/formatting/dates";

type DateCellProps = {
  value: DateValue;
};

// A date is never truncated: its column is fitted to its widest value (#6708).
const unbroken = { whiteSpace: "nowrap" } as const;

/**
 * A date in a list: "Mar 3, 2027", or `NoValueDash` when there is none (#6713). A table cell and a
 * phone card both draw this, so the two cannot disagree.
 */
export function DateCell(props: Readonly<DateCellProps>) {
  const text = formatDate(props.value);
  if (!text) return <NoValueDash />;
  return <time dateTime={isoDateTime(props.value)} style={unbroken}>{text}</time>;
}

/**
 * A moment in a list: "2:15 PM" today, "Oct 9, 2:15 PM" this year, "Mar 3, 2025" before that, with the
 * whole of it in a tooltip (#6713). Every timestamp on the page reads one shared minute clock, so
 * yesterday's rows gain their date together at midnight.
 */
export function TimestampCell(props: Readonly<DateCellProps>) {
  const now = useMinuteClock();
  const text = formatTimestamp(props.value, now);
  if (!text) return <NoValueDash />;
  return (
    <Tooltip title={formatFullTimestamp(props.value)} disableInteractive>
      <time dateTime={isoDateTime(props.value)} style={unbroken}>{text}</time>
    </Tooltip>
  );
}
